import { getSpiffEventName } from "@/activity-feed/constants";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_LABELS,
  SPIFF_RULE_EVENTS_ORDER,
} from "@/spiff-rules/constants";
import type { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { DetailedUser } from "@/user/user.d";
import type {
  ActivityFeedEvent,
  IActivityFeedItem,
  IActivityAttributedUser,
  IActivitySpiff,
  IAttributedUserWithSpiff,
  IEnrichedActivityFeedItem,
} from "./activity-feed.d";

export const getActivityAmount = (activity: IActivityFeedItem): number =>
  // Sum up all spiff amounts in cents, then convert to dollars
  activity.spiffs_json.reduce((sum, spiff) => sum + spiff.amount_cents, 0) /
  100;

/**
 * Checks if an activity is an unattributed review.
 * An unattributed review is a five-star review that has no attributed users
 * and no spiffs created.
 *
 * @param activity The activity feed item to check
 * @returns True if the activity is an unattributed review
 */
export const isUnattributedReview = (activity: IActivityFeedItem): boolean =>
  getSpiffEventName(activity.event_name) ===
    SPIFF_RULE_EVENTS.five_star_review &&
  activity.attributed_users_json.length === 0 &&
  activity.spiffs_json.length === 0;

/**
 * Merges attributed users with their corresponding spiffs.
 * Creates a unified list showing all attributed users and their spiff status.
 * Users without spiffs will have spiff: null.
 *
 * @param attributedUsers Array of attributed users from the activity
 * @param spiffs Array of spiffs from the activity
 * @param currentUserId ID of the currently logged-in user
 * @returns Array of attributed users with their spiff data
 */
export const mergeAttributionsWithSpiffs = (
  attributedUsers: IActivityAttributedUser[],
  spiffs: IActivitySpiff[],
  currentUserId?: string,
): IAttributedUserWithSpiff[] => {
  // Create a map of userId -> spiff for quick lookup
  const spiffsByUserId = new Map<string, IActivitySpiff>();
  spiffs.forEach((spiff) => {
    // If multiple spiffs for the same user, keep the first one (most recent)
    if (!spiffsByUserId.has(spiff.user_id)) {
      spiffsByUserId.set(spiff.user_id, spiff);
    }
  });

  // Start with all attributed users and match with their spiffs
  const merged = attributedUsers.map((user) => {
    const spiff = spiffsByUserId.get(user.id);
    return {
      userId: user.id,
      displayIdentification: user.display_identification,
      isCurrentUser: user.id === currentUserId,
      spiff: spiff
        ? {
            id: spiff.id,
            amountCents: spiff.amount_cents,
            createdAt: spiff.created_at,
            dismissedAt: spiff.dismissed_at,
          }
        : null,
    };
  });

  // Add any spiffs that don't have a corresponding attribution (edge case)
  spiffs.forEach((spiff) => {
    if (!attributedUsers.find((u) => u.id === spiff.user_id)) {
      merged.push({
        userId: spiff.user_id,
        displayIdentification: spiff.user_display_identification,
        isCurrentUser: spiff.user_id === currentUserId,
        spiff: {
          id: spiff.id,
          amountCents: spiff.amount_cents,
          createdAt: spiff.created_at,
          dismissedAt: spiff.dismissed_at,
        },
      });
    }
  });

  // Sort: current user first, then users with spiffs, then attributed users, then dismissed users, then alphabetically
  return merged.sort((a, b) => {
    // 1. Current user always first
    if (a.isCurrentUser) return -1;
    if (b.isCurrentUser) return 1;

    // 2. Then by spiff status (active spiff → no spiff → dismissed spiff)
    const getPriority = (user: IAttributedUserWithSpiff) => {
      if (user.spiff && !user.spiff.dismissedAt) return 1; // active spiff
      if (!user.spiff) return 2; // no spiff
      return 3; // dismissed spiff
    };

    const priorityDiff = getPriority(a) - getPriority(b);
    if (priorityDiff !== 0) return priorityDiff;

    // 3. Finally alphabetically
    return a.displayIdentification.localeCompare(b.displayIdentification);
  });
};

/**
 * Enriches an activity feed item with pre-computed values for UI display.
 * This function consolidates logic that was previously duplicated across components,
 * computing values like total spiff amounts, user attributions, and display names.
 *
 * @param item The raw activity feed item from the API
 * @param currentUser The currently logged-in user
 * @returns An enriched activity feed item with pre-computed display values
 */
export const enrichActivityFeedItemForUser = (
  item: IActivityFeedItem,
  currentUser: DetailedUser | undefined,
): IEnrichedActivityFeedItem => {
  // Calculate total spiff amount
  const totalSpiffAmountCents = item.spiffs_json.reduce(
    (sum, spiff) =>
      sum + (spiff.dismissed_at === null ? spiff.amount_cents : 0),
    0,
  );

  // Calculate display amount (use spiff total if available, otherwise fallback to 0)
  const displayAmount = totalSpiffAmountCents / 100;

  // Count spiffs
  const spiffCount = item.spiffs_json.length;
  const hasMultipleSpiffs = spiffCount > 1;

  // Enrich primary spiff data (first spiff if exists)
  const primarySpiff =
    item.spiffs_json.length > 0
      ? {
          userId: item.spiffs_json[0].user_id,
          displayName:
            item.spiffs_json[0].user_id === currentUser?.id
              ? "You"
              : item.spiffs_json[0].user_display_identification,
          isCurrentUser: item.spiffs_json[0].user_id === currentUser?.id,
        }
      : null;

  // Enrich attribution data
  const attribution =
    item.attributed_users_json.length > 0
      ? (() => {
          const isMultipleUsers = item.attributed_users_json.length > 1;
          const userId = isMultipleUsers
            ? null
            : item.attributed_users_json[0].id;
          const isCurrentUser = userId === currentUser?.id;

          let displayName: string;
          if (isMultipleUsers) {
            displayName = "Multiple users";
          } else if (isCurrentUser) {
            displayName = "You";
          } else {
            displayName = item.attributed_users_json[0].display_identification;
          }

          return {
            userId,
            displayName,
            isCurrentUser,
            isMultipleUsers,
          };
        })()
      : null;

  // Merge attributions with spiffs
  const attributedUsersWithSpiffs = mergeAttributionsWithSpiffs(
    item.attributed_users_json,
    item.spiffs_json,
    currentUser?.id,
  );

  // Map the event name to spiff event name
  const spiff_event_name = getSpiffEventName(
    item.event_name,
  ) as ActivityFeedEvent;

  // Add searchable fields for filtering
  const display_identification = item.details_json.display_identification;
  const searchable_event_label =
    SPIFF_RULE_EVENTS_LABELS[spiff_event_name] || item.event_name;
  const searchable_spiffed_users = item.spiffs_json
    .map((spiff) => spiff.user_display_identification)
    .join(" ");
  const searchable_attributed_users = item.attributed_users_json
    .map((user) => user.display_identification)
    .join(" ");

  const unDismissedUsersWithSpiffs = attributedUsersWithSpiffs.filter(
    (user) => !user.spiff?.dismissedAt && user.spiff,
  );

  return {
    ...item,
    spiff_event_name,
    totalSpiffAmountCents,
    displayAmount,
    spiffCount,
    primarySpiff,
    attribution,
    attributedUsersWithSpiffs,
    unDismissedUsersWithSpiffs,
    hasMultipleSpiffs,
    display_identification,
    searchable_event_label,
    searchable_spiffed_users,
    searchable_attributed_users,
  };
};

export const getOrderedSpiffRuleEvents = (
  events: SpiffRuleEvent[],
): SpiffRuleEvent[] => {
  const eventSet = new Set(events);
  return SPIFF_RULE_EVENTS_ORDER.filter((event) => eventSet.has(event));
};
